import { applyHandHighlights, fmt } from '../../model/tiles.js'
import { TileRow } from '../../tiles/index.js'
import StatusBadge from '../../components/StatusBadge.jsx'
import DoraChip from '../../components/DoraChip.jsx'
import RiichiStick from '../../components/RiichiStick.jsx'
import UkeireCard from '../../components/UkeireCard.jsx'
import FrameNav from '../../components/FrameNav.jsx'

const T = {
  light: {
    page: 'flex min-h-screen items-center justify-center bg-[#efece5] p-6',
    root: 'flex w-[720px] flex-col bg-[#fbf9f4]',
    header: 'flex items-center gap-[14px] bg-[#22563d] px-4 py-[10px] text-[#e8efe6]',
    round: 'text-[15px] font-bold tracking-[2px]',
    wall: (w) => `wall ${w}`,
    wallCls: 'text-[11px] opacity-80',
    sticksCls: 'flex items-center gap-[5px] text-[11px]',
    honba: (h) => `honba ${h}`,
    honbaCls: 'ml-auto text-[11px] opacity-75',
    oppRow: 'flex items-center gap-[10px] border-b border-[#eee6d8] px-4 py-2',
    oppLeft: 'flex w-[148px] flex-none items-center gap-[6px] text-[12px] text-[#4a463c]',
    oppWind: 'font-serif font-bold text-[#8a5a2b]',
    oppName: 'font-semibold',
    oppScore: 'text-[11px] text-[#8a8375]',
    oppDiscards: 'flex flex-1 flex-wrap items-center',
    closed: 'flex-none text-[10px] text-[#a39c8c]',
    heroSection: 'flex flex-col border-t border-[#e2dac8] bg-[#f3efe5] px-4 pb-4 pt-[14px]',
    heroLabelRow: 'flex items-center gap-2 text-[12.5px] text-[#4a463c]',
    heroWind: 'font-serif font-bold text-[#8a5a2b]',
    heroName: 'font-bold',
    heroScore: 'text-[11px] text-[#8a8375]',
    doraW: 18,
    oppTileW: 18,
    heroTileW: 38,
    heroDiscardW: 20,
    ukeireW: 20,
    ukeireTheme: 'light',
    hlc: '#e2a93b',
    footer: 'flex items-start gap-4 bg-[#26303c] px-4 py-[14px] text-[#e7e3d8]',
    footerTitle: 'text-[14px] font-bold text-[#f0c95c]',
    footerBody: 'text-[12.5px] leading-[1.55] opacity-[0.87]',
    footerNavCls: 'w-[34px] h-[30px] rounded-[7px] cursor-pointer text-[13px]',
    footerNavStyle: { border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#e7e3d8' },
    footerHint: (n, t) => `Frame ${n} / ${t} · ← → keys`,
    footerHintCls: 'text-[10.5px] opacity-65',
  },
  industry: {
    page: 'flex min-h-screen items-center justify-center p-6',
    root: 'flex w-[760px] flex-col border border-neutral-300',
    rootStyle: { background: 'var(--color-bg)', color: 'var(--color-text)' },
    header: 'flex items-center gap-[18px] border-b border-neutral-300 px-[18px] py-[10px]',
    round: 'font-heading text-[19px] font-semibold tracking-[2px]',
    wall: (w) => `Wall ${w}`,
    wallCls: 'text-[11px] uppercase tracking-[1.5px] text-neutral-600',
    sticksCls: 'flex items-center gap-[6px] text-[11px] text-neutral-700',
    honba: (h) => `Honba ${h}`,
    honbaCls: 'ml-auto text-[11px] uppercase tracking-[1.5px] text-neutral-600',
    oppRow: 'flex items-center gap-3 border-b border-neutral-200 px-[18px] py-2',
    oppLeft: 'flex w-[150px] flex-none items-center gap-[7px] text-[12.5px]',
    oppWind: 'font-heading font-semibold text-accent-700',
    oppName: 'font-semibold',
    oppScore: 'text-[11px] text-neutral-600',
    oppDiscards: 'flex flex-1 flex-wrap items-center',
    closed: 'flex-none text-[10px] uppercase tracking-[1px] text-neutral-500',
    heroSection: 'relative flex flex-col px-[18px] py-4',
    heroLabelRow: 'flex items-center gap-[9px] text-[13px]',
    heroWind: 'font-heading font-semibold text-accent-700',
    heroName: 'font-bold',
    heroScore: 'text-[11px] text-neutral-600',
    doraW: 18,
    oppTileW: 18,
    heroTileW: 38,
    heroDiscardW: 20,
    ukeireW: 20,
    ukeireTheme: 'industry',
    hlc: 'var(--color-accent)',
    footer: 'blueprint flex items-start gap-[18px] bg-accent-900 px-[18px] py-[14px] text-white',
    footerStyle: { margin: '0 18px 18px' },
    footerKicker: 'font-heading text-[10px] tracking-[3px] opacity-65',
    footerTitle: 'font-heading text-[18px] font-semibold tracking-[0.5px]',
    footerBody: 'text-[12.5px] leading-[1.55] opacity-85',
    footerNavCls: 'w-9 h-[30px] cursor-pointer text-[13px]',
    footerNavStyle: { border: '1px solid rgba(255,255,255,0.4)', background: 'transparent', color: '#fff' },
    footerHint: () => '← → KEYS',
    footerHintCls: 'text-[10px] tracking-[1px] opacity-60',
  },
}

export default function SchematicLayout({ theme, frame, index, total, next, prev }) {
  const m = T[theme]

  const doraTiles = [{ code: frame.doraInd }]
  const heroTiles = applyHandHighlights(frame.hero.hand, frame.hero.hl)
  const heroDraw = frame.hero.draw
    ? [{ code: frame.hero.draw, highlight: !!frame.hero.drawHl }]
    : []

  const opps = ['left', 'top', 'right'].map((k) => frame[k])

  return (
    <div className={m.page}>
      <div className="select-none" style={{ outline: 'none', userSelect: 'none' }}>
        <div className={m.root} style={m.rootStyle}>
          <div className={m.header}>
            <span className={m.round}>{frame.round}</span>
            <span className={m.wallCls}>{m.wall(frame.wall)}</span>
            <DoraChip tiles={doraTiles} width={m.doraW} theme={theme} />
            {frame.sticks > 0 && (
              <span className={m.sticksCls}>
                <RiichiStick width={30} theme={theme} />× {frame.sticks}
              </span>
            )}
            <span className={m.honbaCls}>{m.honba(frame.honba)}</span>
          </div>

          {opps.map((o) => (
            <div key={o.wind} className={m.oppRow}>
              <div className={m.oppLeft}>
                <span className={m.oppWind}>{o.wind}</span>
                <span className={m.oppName}>{o.name}</span>
                <span className={m.oppScore}>{fmt(o.score)}</span>
              </div>
              {o.riichi && <StatusBadge kind="riichi" theme={theme} />}
              <div className={m.oppDiscards}>
                <TileRow tiles={o.discards} width={m.oppTileW} gap={2} highlightColor={m.hlc} />
              </div>
              <div className={m.closed}>closed · 13</div>
            </div>
          ))}

          <div className={m.heroSection} style={{ gap: theme === 'industry' ? 10 : 9 }}>
            <div className={m.heroLabelRow}>
              <span className={m.heroWind}>南</span>
              <span className={m.heroName}>You</span>
              <span className={m.heroScore}>{fmt(frame.hero.score)}</span>
              {frame.hero.riichi && <StatusBadge kind="riichi" theme={theme} />}
              {frame.win && <StatusBadge kind="tsumo" theme={theme} />}
              {frame.ukeire && (
                <UkeireCard
                  theme={m.ukeireTheme}
                  label="WAIT"
                  tiles={frame.ukeire.tiles}
                  note={frame.ukeire.note}
                  width={m.ukeireW}
                  vertical={false}
                  className="ml-auto"
                />
              )}
            </div>
            <TileRow tiles={frame.hero.discards} width={m.heroDiscardW} gap={2} highlightColor={m.hlc} className="flex-wrap" />
            <div className="flex items-end" style={{ gap: 2 }}>
              <TileRow tiles={heroTiles} width={m.heroTileW} gap={2} highlightColor={m.hlc} />
              <div style={{ width: 12 }} />
              <TileRow tiles={heroDraw} width={m.heroTileW} highlightColor={m.hlc} />
            </div>
          </div>

          {theme === 'industry' ? (
            <div className={m.footer} style={m.footerStyle}>
              <i className="corner tl" />
              <i className="corner tr" />
              <i className="corner bl" />
              <i className="corner br" />
              <div className="flex flex-1 flex-col" style={{ gap: 4 }}>
                <div className={m.footerKicker}>FRAME {index + 1} / {total} · CONCEPT</div>
                <div className={m.footerTitle}>{frame.title}</div>
                <div className={m.footerBody}>{frame.body}</div>
              </div>
              <div className="flex flex-none flex-col items-end" style={{ gap: 6 }}>
                <FrameNav
                  theme={theme}
                  prev={prev}
                  next={next}
                  frameNo={index + 1}
                  total={total}
                  showCounter={false}
                  buttonClassName={m.footerNavCls}
                  buttonStyle={m.footerNavStyle}
                />
                <span className={m.footerHintCls}>{m.footerHint(index + 1, total)}</span>
              </div>
            </div>
          ) : (
            <div className={m.footer}>
              <div className="flex flex-1 flex-col" style={{ gap: 4 }}>
                <div className={m.footerTitle}>{frame.title}</div>
                <div className={m.footerBody}>{frame.body}</div>
              </div>
              <div className="flex flex-none flex-col items-end" style={{ gap: 6 }}>
                <FrameNav
                  theme={theme}
                  prev={prev}
                  next={next}
                  frameNo={index + 1}
                  total={total}
                  showCounter={false}
                  buttonClassName={m.footerNavCls}
                  buttonStyle={m.footerNavStyle}
                />
                <span className={m.footerHintCls}>{m.footerHint(index + 1, total)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
